import React, {useEffect, useState} from "react";
import {Modal, Space, Typography, Divider, Input, Select, Tag, Button} from "antd";
import {useTranslation} from "react-i18next";
import api from "../api";

export const RENAME_TOKEN_GROUPS = [
    {
        label: 'Image Name',
        tokens: [
            {value: 'filename', label: 'Filename'},
            {value: 'filename_number_suffix', label: 'Filename Number Suffix'},
            {value: 'folder_name', label: 'Folder Name'},
        ]
    },
    {
        label: 'Sequence & Date',
        tokens: [
            {value: 'global_file_number', label: 'Global File Number', haspadding: true},
            {value: 'sequence_number_in_set', label: 'Number in Set', haspadding: true},
            {value: 'date_yyyymmdd', label: 'Date (YYYYMMDD)'},
            {value: 'date_yymmdd', label: 'Date (YYMMDD)'},
            {value: 'date_yyyy', label: 'Date (YYYY)'},
            {value: 'date_yy', label: 'Date (YY)'},
            {value: 'date_month', label: 'Date (Month)'},
            {value: 'date_mon', label: 'Date (Mon)'},
            {value: 'date_mm', label: 'Date (MM)'},
            {value: 'date_dd', label: 'Date (DD)'},
            {value: 'julian_day', label: 'Julian Day'},
            {value: 'hour', label: 'Hour'},
            {value: 'minute', label: 'Minute'},
            {value: 'second', label: 'Second'},
        ]
    },
    {
        label: 'Metadata',
        tokens: [
            {value: 'title', label: 'Title'},
            {value: 'creator', label: 'Creator'},
            {value: 'iptc_source', label: 'Source'},
        ]
    },
    {
        label: 'Custom',
        tokens: [
            {value: 'custom_text', label: 'Custom Text'},
            {value: 'custom_text_2', label: 'Custom Text 2'},
        ]
    }
];

export const RENAME_TOKEN_MAP = Object.fromEntries(
    RENAME_TOKEN_GROUPS.flatMap(g => g.tokens).map(t => [t.value, t])
);

const RenamePresetEditor = ({visible, onClose, onSave, editPreset}) => {
    const {t} = useTranslation();
    const [name, setName] = useState('');
    const [template, setTemplate] = useState([]);
    const [selectedToken, setSelectedToken] = useState(null);
    const [padding, setPadding] = useState(4);
    const [cmfId, setCmfId] = useState(null);
    const [literalText, setLiteralText] = useState('');
    const [customMetaFields, setCustomMetaFields] = useState([]);

    useEffect(() => {
        if (editPreset) {
            setName(editPreset.name);
            setTemplate(editPreset.template || []);
        } else {
            setName('');
            setTemplate([]);
        }
        setLiteralText('');
        setSelectedToken(null);
        setCmfId(null);
    }, [editPreset, visible]);

    // Load custom meta fields when editor opens
    useEffect(() => {
        if (visible && customMetaFields.length === 0) {
            api('/api/custom_meta_fields?enable_rename=true').then(res => {
                setCustomMetaFields(res.data);
            });
        }
    }, [visible]);

    const insertToken = () => {
        if (!selectedToken) return;
        const segment = {type: 'token', token: selectedToken};
        const tokenDef = RENAME_TOKEN_MAP[selectedToken];
        if (tokenDef?.haspadding) segment.padding = padding;
        setTemplate([...template, segment]);
        setSelectedToken(null);
    };

    const insertCmf = () => {
        if (!cmfId) return;
        const cmf = customMetaFields.find(f => f.id === cmfId);
        if (!cmf) return;
        setTemplate([...template, {type: 'token', token: 'custom_meta_field', custom_meta_field_name: cmf.name}]);
        setCmfId(null);
    };

    const insertLiteral = (value) => {
        if (!value) return;
        setTemplate([...template, {type: 'literal', value}]);
    };

    const removeSegment = (index) => {
        setTemplate(template.filter((_, i) => i !== index));
    };

    const previewTemplate = () => {
        return template.map(seg => {
            if (seg.type === 'literal') return seg.value;
            if (seg.token === 'custom_meta_field') return `{${seg.custom_meta_field_name}}`;
            return `{${RENAME_TOKEN_MAP[seg.token]?.label || seg.token}}`;
        }).join('') + '.ext';
    };

    return (
        <Modal
            open={visible}
            title={editPreset ? t('edit-rename-preset', 'Edit Rename Preset') : t('new-rename-preset', 'New Rename Preset')}
            onCancel={onClose}
            onOk={() => onSave({name, template})}
            okText={t('button-save', 'Save')}
            okButtonProps={{disabled: !name || template.length === 0}}
            width={600}
            modalRender={(modal) => <div onClick={(e) => e.stopPropagation()}>{modal}</div>}
        >
            <Space direction="vertical" size="middle" style={{width: '100%'}}>
                <div>
                    <Typography.Text strong>{t('name', 'Name')}:</Typography.Text>
                    <Input
                        style={{marginTop: 4}}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        placeholder={t('preset-name-placeholder', 'e.g. Date-Sequence')}
                    />
                </div>

                <div>
                    <Typography.Text strong>{t('template', 'Template')}:</Typography.Text>
                    <div style={{
                        border: '1px solid #d9d9d9', borderRadius: 6, padding: 8,
                        minHeight: 40, marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center'
                    }}>
                        {template.map((seg, i) => (
                            seg.type === 'literal' ? (
                                <Tag key={i} closable onClose={() => removeSegment(i)} style={{margin: 0}}>
                                    {seg.value}
                                </Tag>
                            ) : (
                                <Tag key={i} color="blue" closable onClose={() => removeSegment(i)} style={{margin: 0}}>
                                    {seg.token === 'custom_meta_field'
                                        ? seg.custom_meta_field_name
                                        : RENAME_TOKEN_MAP[seg.token]?.label || seg.token}
                                    {seg.padding ? ` (${'0'.repeat(seg.padding - 1)}1)` : ''}
                                </Tag>
                            )
                        ))}
                        {template.length === 0 && <Typography.Text type="secondary">{t('click-insert-tokens', 'Insert tokens below to build your template')}</Typography.Text>}
                    </div>
                </div>

                <div>
                    <Typography.Text type="secondary">{t('example', 'Example')}: {previewTemplate()}</Typography.Text>
                </div>

                <Divider style={{margin: '4px 0'}}/>

                <div>
                    <div><Typography.Text strong>{t('insert-literal-text', 'Insert Text')}:</Typography.Text></div>
                    <Space style={{marginTop: 4}}>
                        <Input
                            size="small"
                            value={literalText}
                            onChange={e => setLiteralText(e.target.value)}
                            placeholder={t('type-text', 'e.g. _ or -')}
                            style={{width: 150}}
                            onPressEnter={() => { insertLiteral(literalText); setLiteralText(''); }}
                        />
                        <Button size="small" onClick={() => { insertLiteral(literalText); setLiteralText(''); }}>
                            {t('insert', 'Insert')}
                        </Button>
                    </Space>
                </div>

                {RENAME_TOKEN_GROUPS.map(group => (
                    <div key={group.label}>
                        <Typography.Text strong>{group.label}:</Typography.Text>
                        <div style={{marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4}}>
                            {group.tokens.map(token => (
                                <Button
                                    key={token.value}
                                    size="small"
                                    type={selectedToken === token.value ? 'primary' : 'default'}
                                    onClick={() => {
                                        if (token.haspadding) {
                                            setSelectedToken(token.value);
                                        } else {
                                            setTemplate([...template, {type: 'token', token: token.value}]);
                                        }
                                    }}
                                >
                                    {token.label}
                                </Button>
                            ))}
                        </div>
                    </div>
                ))}

                {selectedToken && RENAME_TOKEN_MAP[selectedToken]?.haspadding && (
                    <Space>
                        <Typography.Text>{t('zero-padding', 'Zero Padding')}:</Typography.Text>
                        <Select value={padding} onChange={setPadding} style={{width: 100}} size="small">
                            {[2,3,4,5,6,7,8].map(n => <Select.Option key={n} value={n}>{'0'.repeat(n - 1)}1</Select.Option>)}
                        </Select>
                        <Button size="small" type="primary" onClick={insertToken}>{t('insert', 'Insert')}</Button>
                    </Space>
                )}

                <div>
                    <div><Typography.Text strong>{t('custom-metadata-field', 'Custom Metadata Field')}:</Typography.Text></div>
                    <Space style={{marginTop: 4}}>
                        <Select
                            size="small"
                            value={cmfId}
                            onChange={setCmfId}
                            style={{width: 250}}
                            placeholder={t('select-field', 'Select a field...')}
                            options={customMetaFields.map(f => ({label: f.name, value: f.id}))}
                            showSearch
                            optionFilterProp="label"
                        />
                        <Button size="small" type="primary" onClick={insertCmf} disabled={!cmfId}>{t('insert', 'Insert')}</Button>
                    </Space>
                </div>
            </Space>
        </Modal>
    );
};

export default RenamePresetEditor;
